import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import LoadingSpinner from '../common/LoadingSpinner';
import ImageUpload from '../common/ImageUpload';
import SocietySearchableSelect from '../common/SocietySearchableSelect';
import { useCurrentLocation } from '../../hooks/useCurrentLocation';
import clubService from '../../services/clubService';
import { societyService, extractSocietyResults } from '../../services/societyService';

const PlaceCreateForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, user } = useAuth();
  const { showSuccess, showError } = useToast();
  const [loading, setLoading] = useState(false);
  const [loadingSocieties, setLoadingSocieties] = useState(false);
  const [societies, setSocieties] = useState([]);
  const [selectedImages, setSelectedImages] = useState([]);
  const [displayImageIndex, setDisplayImageIndex] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    category: 'community',
    description: '',
    address: '',
    city: '',
    contact_phone: '',
    contact_email: '',
    website: '',
    latitude: '',
    longitude: '',
    hours: '',
    amenities: '',
    special_features: '',
    society: searchParams.get('society') || '',
    is_public: false,
  });
  const { locLoading, captureCurrentLocation } = useCurrentLocation({
    onSuccess: ({ latitude, longitude }) => {
      setFormData((prev) => ({ ...prev, latitude, longitude }));
      showSuccess('Location captured from GPS.');
    },
    onError: (message) => {
      showError(message);
    },
  });

  const categoryOptions = [
    { value: 'community', label: 'Community Center' },
    { value: 'recreation', label: 'Recreation' },
    { value: 'shopping', label: 'Shopping' },
    { value: 'dining', label: 'Dining' },
    { value: 'healthcare', label: 'Healthcare' },
    { value: 'entertainment', label: 'Entertainment' },
    { value: 'sports', label: 'Sports' },
    { value: 'park', label: 'Park' },
    { value: 'library', label: 'Library' },
    { value: 'other', label: 'Other' }
  ];

  useEffect(() => {
    const loadSocieties = async () => {
      try {
        setLoadingSocieties(true);
        const userIsAdmin =
          user &&
          (user.is_staff || user.is_superuser || ['admin', 'moderator'].includes(user.role));
        const societyIdFromUrl = searchParams.get('society');

        if (userIsAdmin) {
          const response = await societyService.getSocieties({ page_size: 500 });
          const allSocieties = extractSocietyResults(response);
          setSocieties(allSocieties);
          if (societyIdFromUrl) {
            const sid = parseInt(societyIdFromUrl, 10);
            const selected = allSocieties.find((s) => s.id === sid);
            if (selected) {
              setFormData((prev) => ({ ...prev, society: String(selected.id) }));
            }
          }
        } else if (user && user.society) {
          setSocieties([user.society]);
          setFormData((prev) => ({
            ...prev,
            society: String(user.society.id),
          }));
        } else if (user) {
          const response = await societyService.getSocieties({ page_size: 500 });
          const allSocieties = extractSocietyResults(response);
          setSocieties(allSocieties);
          if (societyIdFromUrl) {
            const sid = parseInt(societyIdFromUrl, 10);
            const selected = allSocieties.find((s) => s.id === sid);
            if (selected) {
              setFormData((prev) => ({ ...prev, society: String(selected.id) }));
            }
          }
        }
      } catch (error) {
        console.error('Error loading societies:', error);
        showError('Failed to load societies. Please try again.');
      } finally {
        setLoadingSocieties(false);
      }
    };

    if (user) {
      loadSocieties();
    }
  }, [showError, user, searchParams]);

  const buildPlaceDescription = () => {
    const blocks = [formData.description?.trim()];
    const addressLine = [formData.address?.trim(), formData.city?.trim()].filter(Boolean).join(', ');
    if (addressLine) blocks.push(`Address: ${addressLine}`);
    if (formData.contact_phone?.trim()) blocks.push(`Phone: ${formData.contact_phone.trim()}`);
    if (formData.contact_email?.trim()) blocks.push(`Email: ${formData.contact_email.trim()}`);
    if (formData.website?.trim()) blocks.push(`Website: ${formData.website.trim()}`);
    if (formData.hours?.trim()) blocks.push(`Hours:\n${formData.hours.trim()}`);
    if (formData.amenities?.trim()) blocks.push(`Amenities:\n${formData.amenities.trim()}`);
    if (formData.special_features?.trim()) {
      blocks.push(`Special features:\n${formData.special_features.trim()}`);
    }
    return blocks.filter(Boolean).join('\n\n');
  };

  const navigateAfterSubmit = () => {
    const isSocietyContext = location.pathname.includes('/societies/');
    if (isSocietyContext) {
      const pathParts = location.pathname.split('/');
      const societySlugIndex = pathParts.indexOf('societies') + 1;
      const societySlug = pathParts[societySlugIndex];
      navigate(`/societies/${societySlug}/places`);
    } else {
      navigate('/clubs');
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isAuthenticated) {
      showError('Please log in to add a place');
      return;
    }

    if (!formData.society) {
      showError('Please select a society');
      return;
    }

    setLoading(true);
    try {
      const totalImages = selectedImages.length;
      let coverIndex = displayImageIndex;
      if (totalImages > 0 && coverIndex === null) {
        coverIndex = 0;
      }
      if (coverIndex !== null && coverIndex >= totalImages) {
        coverIndex = 0;
      }

      const payload = {
        name: formData.name.trim(),
        society: formData.society,
        category: formData.category,
        description: buildPlaceDescription(),
        is_public: !!formData.is_public,
        images: selectedImages,
      };
      if (coverIndex !== null) {
        payload.display_image_index = coverIndex;
      }
      if (formData.latitude !== '' && formData.longitude !== '') {
        payload.latitude = formData.latitude;
        payload.longitude = formData.longitude;
      }

      await clubService.createClub(payload);
      showSuccess('Place added successfully!');
      navigateAfterSubmit();
    } catch (error) {
      console.error('Error adding place:', error);
      const msg =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        (typeof error.response?.data === 'object'
          ? Object.values(error.response.data).flat().join(' ')
          : null) ||
        'Failed to add place. Please try again.';
      showError(msg);
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-8">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Login Required</h2>
          <p className="text-gray-600 mb-6">Please log in to add a place.</p>
          <button
            onClick={() => navigate('/login')}
            className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition-colors"
          >
            Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border p-8">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Add Place</h2>
        <p className="text-gray-600 mt-1">Add a place of interest to your community</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Name and Category */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
              Place Name *
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter place name"
            />
          </div>

          <div>
            <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-2">
              Category *
            </label>
            <select
              id="category"
              name="category"
              value={formData.category}
              onChange={handleInputChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {categoryOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Description */}
        <div>
          <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
            Description *
          </label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            required
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Describe the place, what it offers, and why it's special"
          />
        </div>

        {/* Address and City */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-2">
              Address *
            </label>
            <input
              type="text"
              id="address"
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter full address"
            />
          </div>

          <div>
            <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-2">
              City *
            </label>
            <input
              type="text"
              id="city"
              name="city"
              value={formData.city}
              onChange={handleInputChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter city"
            />
          </div>
        </div>

        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <span className="block text-sm font-medium text-gray-900">Precise location (optional)</span>
              <span className="block text-xs text-gray-500">
                Use your current GPS location on mobile/portable devices.
              </span>
            </div>
            <button
              type="button"
              onClick={captureCurrentLocation}
              disabled={locLoading}
              className="inline-flex items-center justify-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {locLoading ? 'Getting location…' : 'Use Current Location'}
            </button>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="latitude" className="block text-sm font-medium text-gray-700 mb-1">
                Latitude
              </label>
              <input
                type="text"
                id="latitude"
                name="latitude"
                value={formData.latitude}
                readOnly
                tabIndex={-1}
                autoComplete="off"
                aria-readonly="true"
                placeholder="Use “Use Current Location”"
                className="w-full cursor-default px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-900 focus:outline-none"
              />
            </div>
            <div>
              <label htmlFor="longitude" className="block text-sm font-medium text-gray-700 mb-1">
                Longitude
              </label>
              <input
                type="text"
                id="longitude"
                name="longitude"
                value={formData.longitude}
                readOnly
                tabIndex={-1}
                autoComplete="off"
                aria-readonly="true"
                placeholder="Use “Use Current Location”"
                className="w-full cursor-default px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-900 focus:outline-none"
              />
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label htmlFor="contact_phone" className="block text-sm font-medium text-gray-700 mb-2">
              Contact Phone
            </label>
            <input
              type="tel"
              id="contact_phone"
              name="contact_phone"
              value={formData.contact_phone}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Phone number"
            />
          </div>

          <div>
            <label htmlFor="contact_email" className="block text-sm font-medium text-gray-700 mb-2">
              Contact Email
            </label>
            <input
              type="email"
              id="contact_email"
              name="contact_email"
              value={formData.contact_email}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Email address"
            />
          </div>

          <div>
            <label htmlFor="website" className="block text-sm font-medium text-gray-700 mb-2">
              Website
            </label>
            <input
              type="url"
              id="website"
              name="website"
              value={formData.website}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="https://example.com"
            />
          </div>
        </div>

        {/* Hours */}
        <div>
          <label htmlFor="hours" className="block text-sm font-medium text-gray-700 mb-2">
            Operating Hours
          </label>
          <textarea
            id="hours"
            name="hours"
            value={formData.hours}
            onChange={handleInputChange}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Monday-Friday: 9 AM - 6 PM, Saturday: 10 AM - 4 PM, Sunday: Closed"
          />
        </div>

        {/* Amenities */}
        <div>
          <label htmlFor="amenities" className="block text-sm font-medium text-gray-700 mb-2">
            Amenities
          </label>
          <textarea
            id="amenities"
            name="amenities"
            value={formData.amenities}
            onChange={handleInputChange}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="List available amenities (parking, WiFi, accessibility, etc.)"
          />
        </div>

        {/* Special Features */}
        <div>
          <label htmlFor="special_features" className="block text-sm font-medium text-gray-700 mb-2">
            Special Features
          </label>
          <textarea
            id="special_features"
            name="special_features"
            value={formData.special_features}
            onChange={handleInputChange}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="What makes this place unique? Any special events or features?"
          />
        </div>

        <SocietySearchableSelect
          societies={societies}
          value={formData.society}
          onChange={handleInputChange}
          loading={loadingSocieties}
          showCity
        />

        <div>
          <span className="block text-sm font-medium text-gray-700 mb-2">Photos</span>
          <ImageUpload
            selectedImages={selectedImages}
            onImagesChange={setSelectedImages}
            maxImages={30}
            disabled={loading}
            autoCompress
            displayImageIndex={displayImageIndex}
            onDisplayImageIndexChange={setDisplayImageIndex}
            onError={(msg) => {
              if (msg) showError(msg);
            }}
          />
        </div>

        <div className="flex items-center">
          <input
            type="checkbox"
            id="is_public"
            name="is_public"
            checked={formData.is_public}
            onChange={handleInputChange}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <label htmlFor="is_public" className="ml-2 block text-sm text-gray-700">
            Make this place visible to all users (not just society members)
          </label>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 pt-6">
          <button
            type="submit"
            disabled={loading}
            className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? <LoadingSpinner size="sm" /> : 'Add Place'}
          </button>
          <button
            type="button"
            onClick={navigateAfterSubmit}
            className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default PlaceCreateForm;
